import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useSite } from '@/app/site-context';
import { usePublicPosts } from '@/lib/api/queries';
import { useDocumentHead } from '@/lib/seo/useDocumentHead';
import {
  ButtonLink,
  Card,
  Container,
  Section,
  SectionHeading,
  sectionRhythm,
  Skeleton,
} from '@/components/ui';
import {
  AboutSection,
  CapabilityGrid,
  ContactSection,
  Hero,
  InsightsSection,
  ProcessSection,
  ServiceCards,
  StatsBand,
} from '@/components/marketing/sections';
import { PostCard } from '@/components/content/PostCard';

/**
 * The homepage.
 *
 * Section order and spacing are the reference design's, block for block: hero, capability
 * grid, statistics band, the two orbit features, services, about, the team grid and the
 * closing contact card.
 *
 * The design's "Trusted by" logo marquee sat between the hero and the capability grid. It
 * is gone at the client's request; the `clientLogos` setting and its permission guard are
 * still in place, so it can be brought back without re-deciding any of that.
 *
 * `StatsBand` renders nothing until the client supplies verifiable content, and each
 * remaining block is behind a feature flag the admin controls. The page reads correctly
 * with all of them off, which is what makes shipping without invented proof a viable
 * position rather than a gap.
 *
 * `LatestInsights` is the one block with no counterpart in the design. It is the blog
 * teaser, flag-controlled, and sits where the design goes straight from the team to the
 * contact card.
 */
export function HomePage(): ReactNode {
  const { settings } = useSite();

  useDocumentHead({
    title: `${settings.brand.name} — ${settings.brand.tagline || 'Recruitment boutique'}`,
    description: settings.seoDefaults.defaultMetaDescription,
    canonicalPath: '/',
  });

  return (
    <>
      <Hero />
      <CapabilityGrid />
      <StatsBand />
      <ProcessSection />
      <InsightsSection />
      <ServiceCards />
      <AboutSection />
      {/*
        No team section here.

        The homepage used to close the About block with three faces and a tile linking to
        the full team. Team is a header menu entry now, so the preview was answering a
        question the nav already answers, one scroll further down and with three of ten
        people — and the page it linked to is the one that shows everybody with their
        bios. The team lives at /team.
      */}
      <LatestInsights />
      <ContactSection />
    </>
  );
}

/**
 * The three most recent posts.
 *
 * Returns null when the blog is empty rather than rendering a heading over nothing —
 * an "Insights" section with no insights reads as a broken page on a brand-new install.
 */
function LatestInsights(): ReactNode {
  const { settings } = useSite();
  const { data, isLoading } = usePublicPosts({ page: 1, limit: 3 });

  if (!settings.featureFlags.showBlogOnHomepage) return null;
  if (!isLoading && (!data || data.items.length === 0)) return null;

  return (
    <section className={`bg-surface-wash ${sectionRhythm}`}>
      <Container pad="gutter" className="flex flex-col gap-12">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            align="left"
            eyebrow="Insights"
            title="Notes from the work"
            description="What we learn running searches, written down."
            className="max-w-2xl"
          />
          <ButtonLink to="/blog" variant="secondary" className="shrink-0">
            All insights
            <ArrowRight className="size-4" />
          </ButtonLink>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }, (_, index) => (
                <Card key={index} padding="compact" className="flex flex-col gap-4">
                  <Skeleton className="h-40 w-full rounded-input" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </Card>
              ))
            : data?.items.map((post) => <PostCard key={post.id} post={post} />)}
        </div>
      </Container>
    </section>
  );
}

/**
 * Generic template for the legal pages.
 *
 * Deliberately not pre-filled with boilerplate policy text. A privacy policy is a legal
 * document about how this specific company processes data; generated filler would be
 * both wrong and a compliance liability, and worse, it would look finished. This makes
 * the gap visible so it gets a real answer.
 */
export function LegalPlaceholderPage({ title, path }: { title: string; path: string }): ReactNode {
  useDocumentHead({
    title,
    canonicalPath: `/${path}`,
    // Never index a page that is awaiting its content.
    noIndex: true,
  });

  return (
    <Section className="pt-32 md:pt-40">
      <Container className="max-w-3xl">
        <SectionHeading align="left" as="h1" title={title} />
        <Card className="mt-10 flex flex-col gap-4">
          <p className="text-body">
            This page is awaiting its content. It is intentionally empty rather than filled with
            template text: a policy document has to describe what this business actually does with
            personal data, and placeholder wording that looks finished is worse than an obvious gap.
          </p>
          <p className="text-body">
            An administrator can replace this page with real content from the admin console, or a
            developer can build it as a component. Until then the page is marked{' '}
            <code>noindex</code> so it will not appear in search results.
          </p>
          <p className="text-sm text-muted">
            See <code>docs/CONTENT-BRIEF.md</code> for the outstanding content list.
          </p>
        </Card>
        <div className="mt-8">
          <Link to="/" className="text-sm font-semibold text-primary-strong hover:underline">
            ← Back to home
          </Link>
        </div>
      </Container>
    </Section>
  );
}
